import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a number as currency (EUR).
 *
 * Shows no decimals for round amounts (e.g. "42 €") and two decimals when the
 * amount has a fractional part (e.g. "0,10 €"). This matters for small values
 * like a 4% platform fee on a 1 € booking, which would otherwise round to "0 €".
 *
 * When `freeLabel` is provided and the amount is exactly 0, `freeLabel` is
 * returned instead of "0 €" — useful for free bookings / bookings where we
 * want to display "Free" / "Gratuit".
 */
export function formatCurrency(
  amount: number,
  currency: string = 'EUR',
  locale: string = 'fr-FR',
  freeLabel?: string
): string {
  if (freeLabel && amount === 0) {
    return freeLabel;
  }
  const hasFraction = Math.abs(amount - Math.round(amount)) > 1e-9;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: hasFraction ? 2 : 0
  }).format(amount);
}

export interface FormatStartingPriceOptions {
  locale?: string;
  currency?: string;
  /** Prefix shown when a starting price is known and > 0 (e.g. "from", "dès"). */
  onwardsText?: string;
  /** Label shown when the starting price is exactly 0. */
  freeText?: string;
}

/**
 * Format an activity's starting price into a `{ price, pricePrefix }` pair,
 * or `null` when the price is unknown and the price block should be hidden.
 *
 * Distinguishes three states:
 * - `null` / `undefined` → no priced booking yet → returns `null`. Callers
 *   must skip rendering the price block. This must NOT be shown as "0 €".
 * - `0` → genuinely free → returns `freeText` (or "0 €") with no prefix.
 * - `> 0` → formatted amount with the optional `onwardsText` prefix.
 */
export function formatStartingPrice(
  startingPrice: number | null | undefined,
  { locale = 'fr-FR', currency = 'EUR', onwardsText, freeText }: FormatStartingPriceOptions = {}
): { price: string; pricePrefix: string } | null {
  if (startingPrice == null) {
    return null;
  }
  if (startingPrice === 0) {
    return { price: freeText ?? formatCurrency(0, currency, locale), pricePrefix: '' };
  }
  return {
    price: formatCurrency(startingPrice, currency, locale),
    pricePrefix: onwardsText ?? ''
  };
}
